"use client";

import { reportBrowserFailure } from "@/lib/browser-telemetry";
import { createContext, useCallback, useContext, useEffect } from "react";
import {
  ConvexProviderWithAuth,
  ConvexReactClient,
  useConvexAuth,
} from "convex/react";

const convex = new ConvexReactClient(process.env.NEXT_PUBLIC_CONVEX_URL);
const SessionUserContext = createContext(null);

function useSessionAuth() {
  const userId = useContext(SessionUserContext);
  // Convex uses callback identity to detect account changes. Keep this stable
  // for one user and refresh through the server's HttpOnly Kinde session.
  // react-doctor-disable-next-line react-doctor/react-compiler-no-manual-memoization -- Convex detects account changes by callback identity; this is an auth contract.
  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }) => {
      if (!userId) return null;
      try {
        const response = await fetch("/api/auth/convex-token", {
          method: "POST",
          cache: "no-store",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh: forceRefreshToken === true }),
        });
        if (!response.ok) return null;
        const data = await response.json();
        return typeof data.token === "string" ? data.token : null;
      } catch (error) {
        reportBrowserFailure(error, "auth.convex-token.transport");
        return null;
      }
    },
    [userId],
  );
  return {
    isLoading: false,
    isAuthenticated: Boolean(userId),
    fetchAccessToken,
  };
}
function AuthenticatedConnection({ children, userId }) {
  const { isLoading, isAuthenticated } = useConvexAuth();
  useEffect(() => {
    if (!userId || isLoading || isAuthenticated) return;
    window.location.replace("/api/auth/login");
  }, [isAuthenticated, isLoading, userId]);

  if (!userId || isAuthenticated) return children;
  if (isLoading)
    return (
      <main
        className="flex min-h-dvh items-center justify-center p-8 text-center"
        role="status"
      >
        Connecting securely…
      </main>
    );
  return null;
}
export function ConvexClientProvider({ children, userId }) {
  "use no memo";
  // Convex's supported adapter API accepts a hook as a prop. React Compiler
  // cannot optimize that API shape; keep this tiny integration boundary outside
  // compilation while retaining compilation for the rest of the application.
  return (
    <SessionUserContext.Provider value={userId ?? null}>
      {/* react-doctor-disable-next-line react-hooks-js/hooks -- ConvexProviderWithAuth requires a hook prop; adapter is explicitly outside React Compiler. */}
      <ConvexProviderWithAuth client={convex} useAuth={useSessionAuth}>
        <AuthenticatedConnection userId={userId}>
          {children}
        </AuthenticatedConnection>
      </ConvexProviderWithAuth>
    </SessionUserContext.Provider>
  );
}
